import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Option, Select, Spinner } from '@material-tailwind/react';
import { ModalX } from '../../components';
import { NotifyError } from '../../utils/functions';
import { useAddFloor, useUpdateFloor } from '../../custom-hooks/use-floors';
import { OPERATIONAL_STATUSES_AS_ARRAY } from '../../utils/enums';
import { OPERATIONAL_STATUSES_DESCRIPTION } from '../../utils/enums_descriptions';

const INITIAL_FORM_DATA = {
  number: '',
  floorCode: '',
  usableArea: '',
  totalArea: '',
  status: 'operational',
  description: '',
  meetingRooms: 0,
  restrooms: 0,
  fireSafetyStatus: false,
  powerBackup: false,
  internetAvailability: false,
  climateControl: false,
};

const FloorCrudModal = ({ isOpen, onClose, buildingId, floor = null, existingCount = 0 }) => {
  const isUpdating = !!floor?._id;
  const addFloorMutation = useAddFloor();
  const updateFloorMutation = useUpdateFloor();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  useEffect(() => {
    if (!isOpen) return;
    if (floor) {
      setFormData({
        ...INITIAL_FORM_DATA,
        number: floor.number ?? '',
        floorCode: floor.floorCode || '',
        usableArea: floor.usableArea ?? '',
        totalArea: floor.totalArea ?? '',
        status: floor.status || 'operational',
        description: floor.description || '',
        meetingRooms: floor.meetingRooms || 0,
        restrooms: floor.restrooms || 0,
        fireSafetyStatus: !!floor.fireSafetyStatus,
        powerBackup: !!floor.powerBackup,
        internetAvailability: !!floor.internetAvailability,
        climateControl: !!floor.climateControl,
      });
    } else {
      setFormData({
        ...INITIAL_FORM_DATA,
        number: existingCount + 1,
        floorCode: `F${existingCount + 1}`,
      });
    }
  }, [isOpen, floor, existingCount]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        building: buildingId,
        number: Number(formData.number),
        floorCode: formData.floorCode,
        usableArea: formData.usableArea,
        totalArea: formData.totalArea,
        status: formData.status,
        description: formData.description,
        meetingRooms: Number(formData.meetingRooms) || 0,
        restrooms: Number(formData.restrooms) || 0,
        fireSafetyStatus: formData.fireSafetyStatus,
        powerBackup: formData.powerBackup,
        internetAvailability: formData.internetAvailability,
        climateControl: formData.climateControl,
      };

      if (isUpdating) {
        await updateFloorMutation.mutateAsync({ floorId: floor._id, data: payload });
      } else {
        await addFloorMutation.mutateAsync({ buildingId, data: payload });
      }
      onClose?.();
    } catch (error) {
      NotifyError(error?.response?.data?.message || 'Failed to save floor', error);
    }
  };

  const isPending = addFloorMutation.isLoading || updateFloorMutation.isLoading;

  return (
    <ModalX
      isOpen={isOpen}
      onClose={onClose}
      title={isUpdating ? 'Edit floor' : 'Add floor'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Floor number"
            name="number"
            type="number"
            value={formData.number}
            onChange={handleChange}
            required
          />
          <Input
            label="Floor code"
            name="floorCode"
            value={formData.floorCode}
            onChange={handleChange}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Usable area"
            name="usableArea"
            value={formData.usableArea}
            onChange={handleChange}
          />
          <Input
            label="Total area"
            name="totalArea"
            value={formData.totalArea}
            onChange={handleChange}
          />
        </div>
        <Select
          label="Status"
          value={formData.status}
          onChange={(v) => setFormData((prev) => ({ ...prev, status: v }))}
        >
          {OPERATIONAL_STATUSES_AS_ARRAY.map((status) => (
            <Option key={status} value={status}>
              {OPERATIONAL_STATUSES_DESCRIPTION[status]}
            </Option>
          ))}
        </Select>
        <Input
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
        />
        <div className="flex flex-wrap gap-4">
          <Checkbox
            label="Fire safety"
            name="fireSafetyStatus"
            checked={formData.fireSafetyStatus}
            onChange={handleChange}
          />
          <Checkbox
            label="Power backup"
            name="powerBackup"
            checked={formData.powerBackup}
            onChange={handleChange}
          />
          <Checkbox
            label="Internet"
            name="internetAvailability"
            checked={formData.internetAvailability}
            onChange={handleChange}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="text" onClick={onClose} className="rounded-full">
            Cancel
          </Button>
          <Button type="submit" className="rounded-full" disabled={isPending}>
            {isPending ? <Spinner className="h-4 w-4" /> : isUpdating ? 'Save changes' : 'Add floor'}
          </Button>
        </div>
      </form>
    </ModalX>
  );
};

export default FloorCrudModal;
